import React from "react";

// One palette across every service diagram so they read as a single system.
// Meaning never rests on colour alone: each diagram also carries text labels,
// and shape or weight distinguishes the states a screen reader cannot see.
export const WORKING_FILL = "#DFF8F7";
export const WORKING_STROKE = "#63CEC6";
export const VARIANT_FILL = "#F6BE32";
export const VARIANT_STROKE = "#C08A16";
export const NEUTRAL_FILL = "#FFFFFF";
// Fine for a shape that only bounds something else, like the circle around a
// pair of gene pills — there the pills and the text label carry the meaning.
export const NEUTRAL_STROKE = "#C9D6D5";
// For shapes that *are* the information: in a pedigree, square-versus-circle is
// the only thing encoding sex, so the outline has to meet the 3:1 that WCAG
// 1.4.11 asks of meaningful graphics. NEUTRAL_STROKE is 1.49:1 on white and
// renders as a ghost. This is 3.05:1 — deliberately the same weight as
// VARIANT_STROKE, so the affected symbol still stands out by its fill rather
// than by having a darker outline than everything around it.
export const SYMBOL_STROKE = "#7E9997";
export const LINE = "#B7C6C5";
export const LABEL = "#4A5A59";
export const SURFACE = "#F7FCFC";

// Chinese labels have no spaces and stay on one line; only longer English
// labels need splitting to keep them inside their panel.
export const wrapLabel = (label, maxChars = 14) => {
  const text = String(label == null ? "" : label);
  if (text.length <= maxChars || !text.includes(" ")) return [text];
  const words = text.split(" ");
  let best = 1;
  let bestDiff = Infinity;
  for (let i = 1; i < words.length; i += 1) {
    const diff = Math.abs(
      words.slice(0, i).join(" ").length - words.slice(i).join(" ").length
    );
    if (diff < bestDiff) {
      bestDiff = diff;
      best = i;
    }
  }
  return [words.slice(0, best).join(" "), words.slice(best).join(" ")];
};

// Deterministic offset per fragment index. Math.random would reshuffle the
// scatter on every re-render, including between server and client.
export const jitter = (index, seed, spread) => {
  const n = Math.sin((index + 1) * seed) * 43758.5453;
  return (n - Math.floor(n) - 0.5) * spread;
};

export const LegendItem = ({ swatch, children }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      marginRight: "22px",
      marginBottom: "6px",
      fontSize: "14px",
      color: LABEL,
    }}
  >
    {swatch}
    {children}
  </span>
);

export const Legend = ({ children }) => (
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      margin: "14px 0 20px",
    }}
  >
    {children}
  </div>
);

// Every service diagram shares this shell: section heading, lede, an sr-only
// prose summary carrying the numbers, then the figure body and a caption.
// `bare` is for a figure that sits inside a section which has already titled
// it. Repeating the heading there would both read as a duplicate and put a
// second h2 inside the first one's section. The prose summary and the caption
// are still needed in that case: they describe this figure, not the section.
export const DiagramFigure = ({ base, t, legend, children, bare = false }) => (
  <figure style={{ margin: "0", textAlign: "left" }}>
    {!bare && (
      <>
        {/* A top-level section of the page and a jump-nav target, so it takes
            the same heading level and class as every sibling section. */}
        <h2 className="service-section-title">{t(`${base}.title`)}</h2>
        <p className="service-section-lede">{t(`${base}.intro`)}</p>
      </>
    )}

    <p className="sr-only">{t(`${base}.description`)}</p>

    {legend}
    {children}

    <figcaption style={{ fontSize: "14px", color: "#777", marginTop: "8px" }}>
      {t(`${base}.footnote`)}
    </figcaption>
  </figure>
);
